/**
 * Всплывающее окно в фиксированных координатах.
 *
 * Списки в таблицах обязаны позиционироваться относительно ОКНА, а не ячейки:
 * таблицы прокручиваются по горизонтали и обрезают содержимое по своим
 * границам, поэтому обычный абсолютно спозиционированный список показал бы
 * половину вариантов.
 *
 * При прокрутке и изменении размера окна список ПЕРЕСЧИТЫВАЕТ своё место, а не
 * закрывается. Прежде он закрывался — и на телефоне это делало его вовсе
 * непригодным: поле поиска внутри списка получает фокус, телефон поднимает
 * экранную клавиатуру, клавиатура меняет размер окна, и список закрывал сам
 * себя раньше, чем по нему успевали попасть. На широком экране клавиатуры нет,
 * поэтому там всё выглядело исправным.
 *
 * Закрывается список только тогда, когда держаться больше не за что: кнопка,
 * от которой он открыт, ушла из окна или исчезла из документа.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface PopoverPosition {
  top: number;
  left: number;
  width: number;
  /** Сколько высоты реально доступно — меньше запрошенной на низком окне. */
  height: number;
}

/** Отступ от краёв окна: вплотную к границе список выглядит обрезанным. */
const MARGIN = 8;

/**
 * Видимая область окна.
 *
 * На телефоне клавиатура уменьшает `visualViewport`, но не всегда трогает
 * `innerHeight`. Считать по `innerHeight` значит поместить список под
 * клавиатуру — формально он открыт, а на деле его не видно.
 */
export function viewport(): { width: number; height: number } {
  const visual = typeof window !== 'undefined' ? window.visualViewport : null;
  return {
    width: visual?.width ?? window.innerWidth,
    height: visual?.height ?? window.innerHeight,
  };
}

export function usePopover(size: { width: number; height: number }) {
  const [position, setPosition] = useState<PopoverPosition | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const open = position !== null;

  const close = useCallback(() => setPosition(null), []);

  /**
   * Место для списка рядом с кнопкой. `null` — держаться не за что.
   *
   * `keepOffscreen` меняет поведение, когда кнопка вышла за пределы окна:
   * при прокрутке это значит «ушёл от неё сам» и список закрывается, а при
   * изменении размера окна — что кнопку вытеснила клавиатура. Во втором
   * случае закрывать нельзя: список открывали намеренно и как раз ради ввода.
   */
  const place = useCallback(
    (element: HTMLElement, keepOffscreen = false): PopoverPosition | null => {
      if (!element.isConnected) return null;

      const rect = element.getBoundingClientRect();
      const view = viewport();

      if (!keepOffscreen && (rect.bottom < 0 || rect.top > view.height)) return null;

      const width = Math.min(size.width, view.width - MARGIN * 2);
      const height = Math.min(size.height, view.height - MARGIN * 2);

      // Вниз, если снизу хватает места; иначе вверх, если сверху его больше.
      const below = view.height - rect.bottom;
      const upwards = below < height && rect.top > below;
      const wanted = upwards ? rect.top - height - 4 : rect.bottom + 4;

      return {
        top: Math.max(MARGIN, Math.min(wanted, view.height - height - MARGIN)),
        left: Math.max(MARGIN, Math.min(rect.left, view.width - width - MARGIN)),
        width,
        height,
      };
    },
    [size.width, size.height],
  );

  const openAt = useCallback(
    (element: HTMLElement | null) => {
      if (!element) return;
      triggerRef.current = element;
      setPosition(place(element));
    },
    [place],
  );

  const toggle = useCallback(
    (element: HTMLElement | null) => {
      if (open) close();
      else openAt(element);
    },
    [open, close, openAt],
  );

  useEffect(() => {
    if (!open) return;

    /** Нажатие пришлось внутрь списка или его кнопки? */
    const inside = (target: EventTarget | null | undefined): boolean =>
      target instanceof Node &&
      (popoverRef.current?.contains(target) === true || triggerRef.current?.contains(target) === true);

    const onPointerDown = (event: Event) => {
      if (!inside(event.target)) close();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    /**
     * Пересчёт места. Прокрутка внутри самого списка его не двигает: перечень
     * врачей длинный, и от прокрутки перечня список не должен никуда ехать.
     */
    const move = (keepOffscreen: boolean) => {
      const trigger = triggerRef.current;
      setPosition(trigger ? place(trigger, keepOffscreen) : null);
    };

    /**
     * Прокрутка страницы. Прокрутка внутри самого списка его не двигает:
     * перечень врачей длинный, и от его листания список ехать не должен.
     *
     * Цель проверяется через `instanceof Node`, а не приведением типа: у
     * событий окна цель — сам `window`, узлом он не является, и
     * `contains(window)` бросает исключение прямо в обработчике, молча
     * обрывая пересчёт.
     */
    const onScroll = (event: Event) => {
      const target = event.target;
      if (target instanceof Node && popoverRef.current?.contains(target)) return;
      move(false);
    };

    /** Изменение размера окна — почти всегда экранная клавиатура. */
    const onResize = () => move(true);

    document.addEventListener('mousedown', onPointerDown);
    // На телефоне до `mousedown` дело доходит не всегда: касание по другому
    // списку успевало открыть его, не закрыв этот, и они наслаивались.
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    // Клавиатура и уменьшает видимую область, и смещает её — оба события
    // приходят от `visualViewport` и оба означают «окно изменилось».
    window.visualViewport?.addEventListener('resize', onResize);
    window.visualViewport?.addEventListener('scroll', onResize);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      window.visualViewport?.removeEventListener('resize', onResize);
      window.visualViewport?.removeEventListener('scroll', onResize);
    };
  }, [open, close, place]);

  /**
   * Стили контейнера списка — вместе с предельной высотой.
   *
   * Высоту задаёт сам механизм, а не вызывающий: на низком окне (та же
   * поднятая клавиатура) запрошенные 340 пикселей не помещаются, и список
   * с жёсткой высотой уходил бы под край экрана вместе с кнопкой «ОК».
   */
  const style = position
    ? ({
        position: 'fixed' as const,
        top: position.top,
        left: position.left,
        width: position.width,
        maxHeight: position.height,
        zIndex: 60,
        background: 'var(--otc-surface)',
        borderColor: 'var(--otc-line)',
      } as const)
    : undefined;

  return { open, position, style, popoverRef, openAt, toggle, close };
}

/**
 * Устройство с пальцем вместо мыши.
 *
 * Нужно ровно для одного: не наводить фокус на поле поиска при открытии
 * списка. На широком экране автофокус экономит движение, а на телефоне он
 * поднимает клавиатуру, которая закрывает собой половину списка — притом что
 * искать среди десятка вариантов чаще всего не нужно вовсе.
 */
export function isCoarsePointer(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches === true;
}
